// client/src/components/pages/PeriodSummary/utils.js
//
// Formatting + CSV helpers shared by the Period Summary page (ST-00174).

/**
 * The two series colours. Dispatch grows UP, receipt grows DOWN, everywhere on
 * the page — table sparklines, timeline swimlanes, the overall daily chart and
 * the Day tab of the detail panel all read from these so the eye only has to
 * learn the mapping once. Literal hexes because recharts and inline SVG write
 * presentation attributes, which cannot resolve `var(--…)`; both hues have
 * enough contrast on the light AND the dark surface token.
 */
export const DISPATCH_COLOR = '#45B7AA'; // teal
export const RECEIPT_COLOR = '#F5A623'; // amber

export const GROUP_BY_OPTIONS = [
  { value: 'ref_no', label: 'Ref No' },
  { value: 'order_no', label: 'Order No' },
  { value: 'product', label: 'Product' },
];

export const VIEW_OPTIONS = [
  { value: 'table', label: 'Table' },
  { value: 'timeline', label: 'Timeline' },
];

export const TICKET_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Dispatch', label: 'Dispatch' },
  { value: 'Receipt', label: 'Receipt' },
];

/** Human label for the active grouping key ("Ref No" / "Order No"). */
export const groupByLabel = (groupBy) =>
  GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label || 'Key';

const EM_DASH = '—';

/** Whole numbers with thousands separators; null/undefined render as an em dash. */
export const formatCount = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? EM_DASH
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Tonnes: thousands separators, 1 decimal. */
export const formatTonnes = (value, dp = 1) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? EM_DASH
    : Number(value).toLocaleString(undefined, {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });

/** Compact tonnes (12.3K / 1.2M) for the big KPI numbers. */
export const formatTonnesCompact = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return EM_DASH;
  const n = Number(value);
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (abs >= 10000) return `${(n / 1000).toFixed(1)}K`;
  return formatTonnes(n);
};

/** Percent with a sign, 2 decimals. */
export const formatPct = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? EM_DASH
    : `${Number(value) > 0 ? '+' : ''}${Number(value).toFixed(2)}%`;

/** Compact axis/tick formatter: 1.2M / 12.3K / 950. */
export const formatAxisNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

// ── Calendar helpers ──────────────────────────────────────────────────────────
// Every date the API hands back is a plain SAST calendar string (see the route's
// header comment), so these read the digits directly instead of going through
// `new Date(iso)`, which would re-interpret them in the browser's zone.

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** `YYYY-MM-DD` → `{ year, month, day }` (1-based month). */
export const dayParts = (iso) => {
  const [y, m, d] = String(iso || '').split('-');
  return { year: Number(y), month: Number(m), day: Number(d) };
};

/** Day-of-week (0 = Sunday) for a `YYYY-MM-DD` read as a plain calendar date. */
export const dayOfWeek = (iso) => {
  const { year, month, day } = dayParts(iso);
  if (!year) return null;
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
};

export const isWeekend = (iso) => {
  const dow = dayOfWeek(iso);
  return dow === 0 || dow === 6;
};

/** `2026-08-13` → `13 Aug`. */
export const formatDayShort = (iso) => {
  const { year, month, day } = dayParts(iso);
  if (!year || !month) return EM_DASH;
  return `${day} ${MONTHS[month - 1] || ''}`.trim();
};

/** Short day label for a full timestamp (`…T06:12:00+02:00` → `13 Aug`). */
export const formatTimestampDayShort = (ts) => formatDayShort(String(ts || '').slice(0, 10));

// ── Date-range presets ────────────────────────────────────────────────────────
// Implemented here rather than inside UnifiedDateRangePicker: the shared picker
// is used by a dozen pages that do not want chips, and forking it for one page
// would be a much bigger blast radius than four local helpers.

const atMidnight = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const daysAgo = (n, from = new Date()) => {
  const d = atMidnight(from);
  d.setDate(d.getDate() - n);
  return d;
};

export const DEFAULT_DAYS = 30;

/**
 * Quick ranges offered next to the picker. Every one stays within `bounds="past"`
 * (nothing ends after today), so the picker never rejects what a chip sets.
 */
export const DATE_PRESETS = [
  {
    id: 'thisMonth',
    label: 'This month',
    range: () => {
      const now = atMidnight(new Date());
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    },
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    range: () => {
      const now = atMidnight(new Date());
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0),
      };
    },
  },
  {
    id: 'last7',
    label: 'Last 7 days',
    range: () => ({ start: daysAgo(6), end: atMidnight(new Date()) }),
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    range: () => ({ start: daysAgo(DEFAULT_DAYS - 1), end: atMidnight(new Date()) }),
  },
];

/** The preset id whose range equals `[startStr, endStr]`, or null for a custom range. */
export const activePresetId = (startStr, endStr, toIso) => {
  const match = DATE_PRESETS.find((p) => {
    const { start, end } = p.range();
    return toIso(start) === startStr && toIso(end) === endStr;
  });
  return match ? match.id : null;
};

/**
 * Variance percentage = variance / dispatch × 100. The API supplies
 * `variancePct` per group; this is the fallback (and what the KPI tile uses,
 * since `totals` carries no pct).
 */
export const variancePctOf = (varianceTonnes, dispatchTonnes) => {
  if (varianceTonnes === null || varianceTonnes === undefined) return null;
  if (!dispatchTonnes) return null;
  return (Number(varianceTonnes) / Number(dispatchTonnes)) * 100;
};

/**
 * Colour band for a variance: green under 1%, amber under 3%, red beyond.
 * Unknown (null) variance stays muted. Token-backed Tailwind hues, so this
 * re-themes automatically in dark mode.
 */
export const varianceToneClass = (pct) => {
  if (pct === null || pct === undefined || Number.isNaN(Number(pct))) return 'text-gray-500';
  const abs = Math.abs(Number(pct));
  if (abs < 1) return 'text-green-600';
  if (abs < 3) return 'text-amber-600';
  return 'text-red-600';
};

/** Sorting comparator that keeps nulls last regardless of direction. */
export const compareValues = (a, b, dir) => {
  const nullA = a === null || a === undefined || a === '';
  const nullB = b === null || b === undefined || b === '';
  if (nullA && nullB) return 0;
  if (nullA) return 1;
  if (nullB) return -1;

  let cmp;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else {
    cmp = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }
  return dir === 'asc' ? cmp : -cmp;
};

/** Value a group sorts by for a given column id. */
export const groupSortValue = (group, columnId) => {
  switch (columnId) {
    case 'key':
      return group.key ?? '';
    case 'products':
      return (group.products || []).length;
    case 'routes':
      return (group.routes || []).length;
    case 'hauliers':
      return (group.hauliers || []).length;
    case 'firstTicket':
    case 'lastTicket': {
      const raw = group[columnId];
      if (!raw) return null;
      const t = new Date(raw).getTime();
      return Number.isNaN(t) ? null : t;
    }
    default: {
      const v = group[columnId];
      return v === null || v === undefined ? null : Number(v);
    }
  }
};

/**
 * Label for a route object: the Route Grouping's name when the tickets are on
 * one, else the raw `Dispatch → Receipt` legs (a ticket on no grouping).
 */
export const routeLabel = (route) =>
  route?.name || `${route?.dispatchLocation || '?'} → ${route?.receiptLocation || '?'}`;

/** True when a route object is a Route Grouping rather than raw legs. */
export const isGroupingRoute = (route) =>
  route?.groupingId !== null && route?.groupingId !== undefined;

/**
 * A group is "matched" when it carries BOTH dispatch and receipt tickets — the
 * API nulls `varianceTonnes`/`variancePct` for one-sided keys precisely because
 * a difference between a dispatch and a receipt that never happened is not a
 * variance.
 */
export const isMatchedGroup = (group) =>
  group?.variancePct !== null && group?.variancePct !== undefined;

/**
 * Headline variance over MATCHED keys only.
 *
 * `totals.varianceTonnes` from the API is `SUM(receipt) - SUM(dispatch)` across
 * every key in the period, so a PO dispatched in the period but received after
 * it lands in the headline as a loss of its whole tonnage. On a real month that
 * showed −9% when nothing had actually gone missing. Re-summing client-side over
 * the matched keys gives the number an operator can act on, and the one-sided
 * count says how much of the period the figure deliberately leaves out.
 *
 * @param {Array} groups rows as returned by GET /api/period-summary
 * @returns {{matchedKeys:number, oneSidedKeys:number, dispatchTonnes:number,
 *            receiptTonnes:number, varianceTonnes:number, variancePct:(number|null)}}
 */
export const matchedVariance = (groups = []) => {
  let matchedKeys = 0;
  let oneSidedKeys = 0;
  let dispatchTonnes = 0;
  let receiptTonnes = 0;

  groups.forEach((g) => {
    if (isMatchedGroup(g)) {
      matchedKeys += 1;
      dispatchTonnes += Number(g.dispatchTonnes) || 0;
      receiptTonnes += Number(g.receiptTonnes) || 0;
    } else {
      oneSidedKeys += 1;
    }
  });

  const varianceTonnes = receiptTonnes - dispatchTonnes;
  return {
    matchedKeys,
    oneSidedKeys,
    dispatchTonnes,
    receiptTonnes,
    varianceTonnes: matchedKeys ? varianceTonnes : null,
    variancePct: matchedKeys ? variancePctOf(varianceTonnes, dispatchTonnes) : null,
  };
};

/**
 * Timeline payload → `Map(key → { days, max })` for the table's Activity
 * sparkline. `days` stays SPARSE (active days only, exactly as the API sends
 * it); the sparkline places each one on the period's calendar by date, so a
 * 31-day period costs ~31 nodes per row instead of one per calendar cell.
 */
export const buildSparklineMap = (timeline) => {
  const map = new Map();
  (timeline?.groups || []).forEach((g) => {
    let max = 0;
    (g.days || []).forEach((d) => {
      max = Math.max(max, Number(d.dispatchTonnes) || 0, Number(d.receiptTonnes) || 0);
    });
    map.set(g.key, { days: g.days || [], max });
  });
  return map;
};

// ── CSV export ────────────────────────────────────────────────────────────────

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export const CSV_COLUMNS = [
  { header: 'Key', get: (g) => g.key },
  { header: 'Tickets', get: (g) => g.tickets },
  { header: 'Dispatch Tickets', get: (g) => g.dispatchTickets },
  { header: 'Receipt Tickets', get: (g) => g.receiptTickets },
  { header: 'Nett Tonnes', get: (g) => g.nettTonnes },
  { header: 'Avg Nett Tonnes', get: (g) => g.avgNettTonnes },
  { header: 'Dispatch Tonnes', get: (g) => g.dispatchTonnes },
  { header: 'Receipt Tonnes', get: (g) => g.receiptTonnes },
  { header: 'Variance Tonnes', get: (g) => g.varianceTonnes },
  {
    header: 'Variance %',
    get: (g) => g.variancePct ?? variancePctOf(g.varianceTonnes, g.dispatchTonnes),
  },
  { header: 'Products', get: (g) => (g.products || []).join(' | ') },
  { header: 'Routes', get: (g) => (g.routes || []).map(routeLabel).join(' | ') },
  { header: 'Hauliers', get: (g) => (g.hauliers || []).join(' | ') },
  { header: 'Vehicles', get: (g) => g.vehicleCount },
  { header: 'Drivers', get: (g) => g.driverCount },
  { header: 'First Ticket', get: (g) => g.firstTicket },
  { header: 'Last Ticket', get: (g) => g.lastTicket },
  { header: 'Active Days', get: (g) => g.activeDays },
  { header: 'Amended Tickets', get: (g) => g.amendedTickets },
];

/**
 * Build the CSV text for the rows currently on screen. `keyLabel` names the
 * first column after whatever the page is grouped by ("Ref No" / "Order No" /
 * "Product"), so the export is self-describing.
 */
export const buildCsv = (groups = [], keyLabel = 'Key') => {
  const header = CSV_COLUMNS.map((c, i) => csvCell(i === 0 ? keyLabel : c.header)).join(',');
  const body = groups.map((g) => CSV_COLUMNS.map((c) => csvCell(c.get(g))).join(','));
  return [header, ...body].join('\r\n');
};

/**
 * Trigger a client-side CSV download. Guarded because jsdom (and locked-down
 * browsers) may not implement createObjectURL.
 */
export const downloadCsv = (filename, csvText) => {
  try {
    const blob = new Blob(['﻿', csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch {
    return false;
  }
};

// ── URL state ─────────────────────────────────────────────────────────────────

/**
 * `YYYY-MM-DD` → a local midnight `Date`, or null. Parsed part-by-part rather
 * than `new Date('2026-08-01')`, which JS reads as UTC and would shift the day
 * for anyone west of Greenwich.
 */
export const parseIsoDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
  const { year, month, day } = dayParts(value);
  const d = new Date(year, month - 1, day);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Pick the first value that is one of `options`' values. */
export const firstValidOption = (options, ...candidates) => {
  const found = candidates.find(
    (c) => c !== null && c !== undefined && options.some((o) => o.value === c)
  );
  return found === undefined ? null : found;
};

// ── Preferences ───────────────────────────────────────────────────────────────

export const PREFS_KEY = 'periodSummary.prefs';

export const readPrefs = () => {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const writePrefs = (prefs) => {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage unavailable (private mode / blocked) — preferences are a
    // convenience, never worth surfacing an error for.
  }
};
